import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, isNil, startCase } from 'lodash'
import { differenceInDays } from 'date-fns'
import { IconCalendarClock } from '@tabler/icons-react'

import { Card } from '@/components/cards/Card'
import { AnalyticsCard } from '@/components/cards/AnalyticsCard'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import { Pill } from '@/components/pills/Pill'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { ExternalLink } from '@/components/ExternalLink'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import LineChart from '@/components/charts/LineChart'
import { CHART } from '@/components/charts/chartPalette'
import { ListeningStatus } from '@/views/CommandCenter/components/ListeningStatus'
import {
  fetchVoiceAgentSummary,
  fetchVoiceAgentCallVolume,
  fetchVoiceAgentByAgent,
  fetchVoiceAgentRecentCalls,
  streamActiveCalls,
} from '@/reducers/voiceAgents/voiceAgentsAnalytics.actions'
import { ACTIVE_CALLS_STATUS } from '@/reducers/voiceAgents/voiceAgentsAnalytics.redux'
import { formatSeconds } from '@/utils/helpers'
import {
  computeDelta,
  transferRate,
  toVolumeSeries,
  toAgentRow,
  toRecentCallRow,
  toDisconnectionBars,
  buildCallExplorerLink,
  activeCallCount,
} from '@/reducers/voiceAgents/voiceAgentsAnalytics.helpers'

import {
  VOLUME_GRANULARITIES,
  DEFAULT_GRANULARITY,
  DISCONNECTION_REASONS,
} from '../voiceAgentsAnalytics.constants'

import '../VoiceAgentsAnalytics.scss'

// Disposition / disconnect labels map to a Pill variant so the same wording is
// colored consistently across both tables.
const dispositionPill = (disposition) => {
  const variants = {
    Connected: { success: true },
    Transferred: { informative: true },
    Voicemail: { emphasized: true },
    'No Answer': { warning: true },
  }
  return (
    <Pill small {...(variants[disposition] || { emphasized: true })}>
      {disposition}
    </Pill>
  )
}

// Reason enum -> the same label the Disconnection reason card uses, so a reason
// reads identically in the chart and in the table beneath it.
const DISCONNECT_LABELS = Object.fromEntries(
  DISCONNECTION_REASONS.map((reason) => [reason.apiKey, reason.label])
)

// Em dash means the agent's calls recorded no reason. A reason this list doesn't
// know yet falls back to startCase ("Dial Busy") rather than blanking the cell —
// a silent em dash is the bug this column already had once.
const disconnectLabel = (value) => {
  if (isNil(value)) {
    return '—'
  }
  return DISCONNECT_LABELS[value] || startCase(value)
}

const AGENT_COLUMNS = [
  {
    accessor: 'name',
    label: 'AI Agent',
    format: (value) => <span className="table-link">{value}</span>,
  },
  { accessor: 'calls', label: 'Calls' },
  { accessor: 'transferRate', label: 'Transfer Rate', format: (value) => `${value}%` },
  { accessor: 'avgHandleTime', label: 'Avg Handle Time' },
  { accessor: 'topDisconnect', label: 'Top Disconnect', format: disconnectLabel },
]

const RECENT_CALL_COLUMNS = [
  {
    accessor: 'id',
    label: 'Call ID',
    // Deep-link to the individual call in Call Explorer (opens in a new tab).
    format: (value) => (
      <ExternalLink url={`/calls/${value}`} label={value} className="table-link" />
    ),
  },
  { accessor: 'agent', label: 'Agent' },
  {
    accessor: 'voipCallId',
    label: 'VOIP Call ID',
    // Calendar icon marks calls that originated from a scheduled call.
    format: (value, row) => (
      <span className="flex-align-center small-gap">
        {value}
        {row.isScheduled && (
          <InteractiveTooltip content="Scheduled call">
            <IconCalendarClock size={16} />
          </InteractiveTooltip>
        )}
      </span>
    ),
  },
  { accessor: 'disposition', label: 'Disposition', format: dispositionPill },
  { accessor: 'disconnect', label: 'Disconnect' },
  { accessor: 'dateTime', label: 'Date Time' },
  { accessor: 'duration', label: 'Duration' },
]

export const VoiceAgentsAnalytics = ({
  orgId,
  agentId,
  direction,
  scheduledFilter = 'all',
  startDate,
  endDate,
  agentUserIds,
}) => {
  const dispatch = useDispatch()
  const [granularity, setGranularity] = useState(DEFAULT_GRANULARITY)

  const { summary, callVolume, byAgent, recentCalls, loading, error } = useSelector(
    (state) => state.voiceAgentsAnalytics
  )
  const provisioningAgents = useSelector((state) => state.agentProvisioning.agents.items)
  const activeCallItems = useSelector((state) => state.voiceAgentsAnalytics.activeCalls.items)
  const activeCallsStatus = useSelector((state) => state.voiceAgentsAnalytics.activeCalls.status)

  // Summary, by-agent, and recent-calls refetch when the filters change.
  useEffect(() => {
    const filters = { orgId, agentId, direction, scheduled: scheduledFilter, startDate, endDate }
    dispatch(fetchVoiceAgentSummary(filters))
    dispatch(fetchVoiceAgentByAgent(filters))
    dispatch(fetchVoiceAgentRecentCalls(filters))
  }, [dispatch, orgId, agentId, direction, scheduledFilter, startDate, endDate])

  // Live counts (SSE), scoped to org only — no date range; agent filter is FE-side.
  useEffect(() => {
    const controller = new AbortController()
    dispatch(streamActiveCalls(orgId, controller.signal))
    return () => controller.abort()
  }, [dispatch, orgId])

  // Call volume additionally refetches on granularity change (the BE buckets
  // server-side), so it gets its own effect.
  useEffect(() => {
    dispatch(
      fetchVoiceAgentCallVolume(
        { orgId, agentId, direction, scheduled: scheduledFilter, startDate, endDate },
        granularity
      )
    )
  }, [dispatch, orgId, agentId, direction, scheduledFilter, startDate, endDate, granularity])

  // The summary endpoint compares against an equal-length preceding window, so
  // the delta caption names the selected span (inclusive of both endpoints).
  const days = differenceInDays(endDate, startDate) + 1
  const priorDescription = `vs prior ${days} days`

  const callsDelta = summary ? computeDelta(summary.calls_handled, summary.prior_calls_handled) : {}
  const ahtDelta = summary
    ? computeDelta(summary.avg_handle_time_seconds, summary.prior_avg_handle_time_seconds, {
        lowerIsBetter: true,
      })
    : {}
  const rate = transferRate(summary?.transferred, summary?.calls_handled)
  const callsHandledLabel = isNil(summary?.calls_handled)
    ? '0'
    : summary.calls_handled.toLocaleString()
  const transferDescription = isNil(summary?.calls_handled)
    ? '0 of 0 calls'
    : `${summary.transferred} of ${summary.calls_handled} calls`

  const volumeSeries = toVolumeSeries(callVolume, granularity)
  const agentRows = byAgent.map(toAgentRow)
  const recentRows = recentCalls.items.map(toRecentCallRow)
  const disconnectionBars = toDisconnectionBars(
    summary?.disconnection_reasons,
    DISCONNECTION_REASONS,
    summary?.calls_handled
  )

  const liveActiveCalls = activeCallCount({
    activeCallItems,
    provisioningAgents,
    agentId,
    agentUserIds,
  })

  // Bubble: green (pulsing when calls are live) once CONNECTED, grey otherwise —
  // with a hover explaining the grey so the user knows the count may be stale.
  const liveConnected = activeCallsStatus === ACTIVE_CALLS_STATUS.CONNECTED
  let liveDotStatus = 'offline'
  if (liveConnected) {
    liveDotStatus = liveActiveCalls > 0 ? 'on_call' : 'online'
  }
  let liveTooltip = null
  if (activeCallsStatus === ACTIVE_CALLS_STATUS.DISCONNECTED) {
    liveTooltip = 'Live updates disconnected — refresh the page to reconnect.'
  } else if (activeCallsStatus === ACTIVE_CALLS_STATUS.CONNECTING) {
    liveTooltip = 'Connecting to live updates…'
  }

  // The endpoint returns a bounded page; when the org/window has more calls than
  // we fetched, say so and point users to Call Explorer for the full set.
  const recentCallsCaption =
    recentCalls.total > recentCalls.items.length
      ? `Showing the ${recentCalls.items.length} most recent of ${recentCalls.total.toLocaleString()} calls — open Call Explorer to see all.`
      : null

  // Deep-link Call Explorer to the same filtered window, scoped to the voice
  // agent(s) so it shows the full result set (not just the rows fetched here)
  // and never any calls outside these agents.
  const callExplorerLink = buildCallExplorerLink({
    orgId,
    agentId,
    direction,
    startDate,
    endDate,
    agentUserIds,
  })

  return (
    <div className="va-analytics" data-testid="voice-agents-analytics">
      {error.summary ? (
        <ErrorMessage content={error.summary} />
      ) : (
        <div className="analytics-card-grid">
          <AnalyticsCard
            description="Calls handled"
            data={callsHandledLabel}
            dateDescription={priorDescription}
            variant={callsDelta.variant}
            percent={callsDelta.percent}
            arrowUp={callsDelta.arrowUp}
            loading={loading.summary}
          />
          <AnalyticsCard
            description="Transfer rate"
            data={`${rate}%`}
            dateDescription={transferDescription}
            loading={loading.summary}
          />
          <AnalyticsCard
            description="Avg handle time"
            data={summary ? formatSeconds(summary.avg_handle_time_seconds) : '0s'}
            dateDescription={priorDescription}
            variant={ahtDelta.variant}
            percent={ahtDelta.percent}
            arrowUp={ahtDelta.arrowUp}
            loading={loading.summary}
          />
          <AnalyticsCard
            testId="analytics-card-active-calls"
            description={
              <span className="va-analytics__active-calls-label">
                Active calls
                <InteractiveTooltip content={liveTooltip} hide={isNil(liveTooltip)}>
                  <ListeningStatus status={liveDotStatus} />
                </InteractiveTooltip>
              </span>
            }
            data={liveActiveCalls}
            hideDateDescription
          />
        </div>
      )}

      <div className="va-analytics__split">
        <Card
          title="Call volume"
          className="va-analytics__volume-card"
          buttons={
            <SegmentedControl
              small
              items={VOLUME_GRANULARITIES.map((item) => ({
                accessor: item.accessor,
                label: item.label,
                active: granularity === item.accessor,
                onClick: () => setGranularity(item.accessor),
              }))}
            />
          }
        >
          <div className="va-analytics__chart">
            {error.callVolume ? (
              <ErrorMessage content={error.callVolume} />
            ) : isEmpty(volumeSeries?.[0]?.data) ? (
              <div className="va-analytics__empty">No call volume data yet</div>
            ) : (
              <LineChart
                data={volumeSeries}
                colors={[CHART.BRAND]}
                enableArea
                areaOpacity={0.08}
                axisBottom={{ tickSize: 0, tickPadding: 12 }}
                axisLeft={{
                  tickSize: 0,
                  tickPadding: 8,
                  legend: 'Calls',
                  legendOffset: -60,
                  minInterval: 1,
                  format: (value) => Number(value).toLocaleString(),
                }}
              />
            )}
          </div>
        </Card>

        <Card title="Disconnection reason" className="va-analytics__disconnect-card">
          <ul className="va-analytics__disconnect-list">
            {disconnectionBars.map((reason) => {
              const { percent } = reason
              return (
                <li key={reason.key} className="va-analytics__disconnect-item">
                  <div className="va-analytics__disconnect-head">
                    <span className="va-analytics__disconnect-label">{reason.label}</span>
                    <span className="va-analytics__disconnect-percent">{percent}%</span>
                  </div>
                  <div className="va-analytics__disconnect-track">
                    <div
                      className="va-analytics__disconnect-fill"
                      style={{ '--bar-width': `${percent}%`, '--bar-color': reason.color }}
                    />
                  </div>
                </li>
              )
            })}
          </ul>
        </Card>
      </div>

      <Card
        title="Performance by agent"
        className="va-analytics__table-card"
        buttons={<span className="va-analytics__card-caption">{priorDescription}</span>}
      >
        {error.byAgent ? (
          <ErrorMessage content={error.byAgent} />
        ) : (
          <AdvancedTable
            testid="voice-agents-performance-table"
            loading={loading.byAgent}
            rows={agentRows}
            columns={AGENT_COLUMNS}
            defaultOrderBy="calls"
            defaultOrder="desc"
            noDataHeader="No agent performance yet"
            noDataText="Agent metrics will appear here once calls are processed."
          />
        )}
      </Card>

      <Card
        title="Recent calls"
        className="va-analytics__table-card"
        buttons={
          !isEmpty(recentRows) && (
            <ExternalLink
              url={callExplorerLink}
              label="View all in Call Explorer"
              id="voice-agents-recent-calls"
            />
          )
        }
      >
        {error.recentCalls ? (
          <ErrorMessage content={error.recentCalls} />
        ) : (
          <AdvancedTable
            testid="voice-agents-recent-calls-table"
            loading={loading.recentCalls}
            rows={recentRows}
            columns={RECENT_CALL_COLUMNS}
            defaultOrderBy="dateTime"
            defaultOrder="desc"
            sortEnabled={false}
            pagination
            rowsPerPage={30}
            enableUpdateRowsPerPage={false}
            itemName="call"
            bottomCaption={recentCallsCaption}
            noDataHeader="No recent calls"
            noDataText="Recent calls will appear here once your agents start handling calls."
          />
        )}
      </Card>
    </div>
  )
}

export default VoiceAgentsAnalytics
